import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { useUserPresenceStatus } from "hooks/useConversations";
import {
  ContactDto,
  UserPresenceResponseDtoStatusEnum,
} from "libs/cellera-sdk";
import { useMemo } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import BadgeAvatar from "./BadgeAvatars";

interface ContactItemProps {
  contact: ContactDto;
  isSelected: boolean;
  onSelect: (contact: ContactDto) => void;
  onDelete: (contactId: string) => void;
}

const ContactItem: React.FC<ContactItemProps> = ({
  contact,
  isSelected,
  onSelect,
  onDelete,
}: ContactItemProps) => {
  const { colors } = useCelleraTheme();

  const { firstName, lastName, photoUrl, contactId } = contact;

  const { data: userPresenceStatus } = useUserPresenceStatus(contactId);

  const contactName = useMemo(() => {
    const parts = [firstName];
    parts.push(lastName);
    return parts.join(" ").trim() || "N/A";
  }, [firstName, lastName]);

  return (
    <ListItem
      disablePadding
      sx={{
        borderBottom: `1px solid ${colors.border.light}`,
      }}
    >
      <ListItemButton
        className="rounded-2xl h-18 gap-3"
        sx={{
          backgroundColor: isSelected
            ? colors.background.grayLight
            : "transparent",
          display: "flex",
          alignItems: "center",
        }}
        onClick={() => onSelect(contact)}
      >
        <BadgeAvatar
          photoUrl={photoUrl}
          presenceStatus={
            userPresenceStatus?.status ??
            UserPresenceResponseDtoStatusEnum.Offline
          }
        />

        <ListItemText
          primary={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  color: colors.text.primary,
                }}
              >
                {contactName}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 400,
                  fontSize: "12px",
                  color: colors.text.primary,
                }}
              ></Typography>
            </Box>
          }
          secondary={
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: "12px",
                color: colors.text.secondary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            ></Typography>
          }
        />

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(contactId);
          }}
        >
          <DeleteIcon color="error" fontSize="small" />
        </IconButton>
      </ListItemButton>
    </ListItem>
  );
};

export default ContactItem;
